import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  AccordionDetails,
  Divider,
  Stack
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  Block as BlockIcon,
  WarningAmberRounded as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';

import CustomAccordion from '../../../CustomAccordion';
import InfoGeneralForm from '../../formsAjustes/InfoGeneralForm';
import DatosLocacionForm from '../../formsAjustes/DatosLocacionForm';
import InformacionContactoForm from '../../formsAjustes/InformacionContactoForm';
import RedesSocialesForm from '../../formsAjustes/RedesSocialesForm';
import PersonalizacionTemaForm from '../../formsAjustes/PersonalizacionTemaForm';
import PrevisualizacionTema from '../../formsAjustes/PrevisualizacionTema';

import { storage } from '../../../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import optimizeAndConvertToWebP from '../../../../utils/optimizeAndConvertToWebP';
import SigertreLoader from '../../../Common/SigertreLoader';

/* ────────────────────────────────────────────────────────────────────────────
   Helpers visuales para CONSISTENCIA (pill buttons)
   ──────────────────────────────────────────────────────────────────────────── */
const pillButton = (t) => ({
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 700,
  px: 2.25,
  py: 0.9,
  boxShadow: 'none',
});

const outlinePill = (t, colorKey = 'primary') => ({
  ...pillButton(t),
  bgcolor: 'transparent',
  border: `1px solid ${t.palette[colorKey].main}33`,
  '&:hover': { borderColor: t.palette[colorKey].main, bgcolor: `${t.palette[colorKey].main}0f` },
});

const containedPill = (t, colorKey = 'primary') => ({
  ...pillButton(t),
  bgcolor: t.palette[colorKey].main,
  '&:hover': { bgcolor: t.palette[colorKey].dark },
});

/* ──────────────────────────────────────────────────────────────────────────── */

const AjustesManager = ({ competencia, setCompetencia, onDelete, setSnackbar: showSnackbar }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState(null);
  const [imagenFile, setImagenFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isOptimizingLogo, setIsOptimizingLogo] = useState(false);
  const [isOptimizingPortada, setIsOptimizingPortada] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingPortada, setIsUploadingPortada] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ logo: 0, portada: 0 });
  const [errors, setErrors] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false); // 👈 nuevo

  const logoInputRef = useRef(null);
  const portadaInputRef = useRef(null);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        if (typeof dateString === 'object' && dateString !== null && typeof dateString.toDate === 'function') {
          const firestoreDate = dateString.toDate();
          if (!isNaN(firestoreDate.getTime())) {
            const year = firestoreDate.getUTCFullYear();
            const month = String(firestoreDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(firestoreDate.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        }
        return '';
      }
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (competencia && typeof competencia === 'object' && competencia.id) {
      setFormData({
        general: {
          ...(competencia.general || {}),
          fechaInicioCompetencia: formatDateForInput(competencia.general?.fechaInicioCompetencia),
          fechaFinCompetencia: formatDateForInput(competencia.general?.fechaFinCompetencia),
          fechaInicioInscripciones: formatDateForInput(competencia.general?.fechaInicioInscripciones),
          fechaFinInscripciones: formatDateForInput(competencia.general?.fechaFinInscripciones),
          precioInscripciones: competencia.general?.precioInscripciones?.toString() ?? '0',
          logo: competencia.general?.logo ?? '',
          imagenPortada: competencia.general?.imagenPortada ?? '',
        },
        convocatoria: { ...(competencia.convocatoria || {}) },
        location: { ...(competencia.location || {}) },
        contact: { ...(competencia.contact || {}), contactos: Array.isArray(competencia.contact?.contactos) ? competencia.contact.contactos : [] },
        social: { ...(competencia.social || {}) },
        theme: { ...(competencia.theme || {}), colorPrimario: competencia.theme?.colorPrimario || '#1976d2', colorSecundario: competencia.theme?.colorSecundario || '#dc004e' },
        rules: { ...(competencia.rules || {}) },
        registration: { ...(competencia.registration || {}) }
      });
      setImagenFile(null);
      setLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = null;
      if (portadaInputRef.current) portadaInputRef.current.value = null;
    } else {
      setFormData(null);
    }
  }, [competencia]);

  const handleSectionChange = useCallback((section, name, value) => {
    setFormData((prev) => {
      if (!prev) return null;
      if (section === 'contact' && name === 'contactos') {
        return { ...prev, contact: { ...(prev.contact || {}), contactos: value } };
      }
      return { ...prev, [section]: { ...(prev[section] || {}), [name]: value } };
    });
  }, []);

  const handleChangeGeneral = useCallback((name, value) => handleSectionChange('general', name, value), [handleSectionChange]);
  const handleChangeLocation = useCallback((name, value) => handleSectionChange('location', name, value), [handleSectionChange]);
  const handleChangeSocial = useCallback((name, value) => handleSectionChange('social', name, value), [handleSectionChange]);
  const handleChangeTheme = useCallback((name, value) => handleSectionChange('theme', name, value), [handleSectionChange]);

  const handleAddContacto = useCallback(() => {
    setFormData((prev) => {
      if (!prev) return null;
      const currentContactos = prev.contact?.contactos || [];
      if (currentContactos.length >= 3) {
        showSnackbar({ open: true, message: 'Solo puedes añadir hasta 3 contactos.', severity: 'warning' });
        return prev;
      }
      const newContactos = [...currentContactos, { nombre: '', email: '', telefono: '' }];
      return { ...prev, contact: { ...(prev.contact || {}), contactos: newContactos } };
    });
  }, [showSnackbar]);

  const handleContactoChange = useCallback((index, field, value) => {
    setFormData((prev) => {
      if (!prev || !prev.contact?.contactos || !prev.contact.contactos[index]) return prev;
      const updatedContactos = prev.contact.contactos.map((contacto, i) =>
        i === index ? { ...contacto, [field]: value } : contacto
      );
      return { ...prev, contact: { ...prev.contact, contactos: updatedContactos } };
    });
  }, []);

  const handleDeleteContacto = useCallback((index) => {
    setFormData((prev) => {
      if (!prev || !prev.contact?.contactos) return prev;
      const currentContactos = prev.contact.contactos;
      if (currentContactos.length > 1 && index >= 0 && index < currentContactos.length) {
        const newContactos = currentContactos.filter((_, i) => i !== index);
        return { ...prev, contact: { ...prev.contact, contactos: newContactos } };
      } else if (currentContactos.length === 1) {
        showSnackbar({ open: true, message: 'Debe haber al menos un contacto.', severity: 'warning' });
        return prev;
      }
      return prev;
    });
  }, [showSnackbar]);

  const handleFileChangeAndOptimize = async (event, fileTypeSetter, optimizingSetter, inputRef) => {
    const file = event.target.files?.[0];
    fileTypeSetter(null);
    if (inputRef.current) inputRef.current.value = null;

    if (file && file.type.startsWith('image/')) {
      optimizingSetter(true);
      showSnackbar({ open: true, message: 'Optimizando imagen...', severity: 'info' });
      try {
        const optimizationResult = await optimizeAndConvertToWebP(file);
        if (optimizationResult && optimizationResult.file) {
          fileTypeSetter(optimizationResult.file);
          showSnackbar({ open: true, message: 'Imagen optimizada lista.', severity: 'success' });
        } else {
          throw new Error('La optimización no devolvió un archivo.');
        }
      } catch (error) {
        showSnackbar({ open: true, message: `Error al optimizar imagen: ${error.message}`, severity: 'error' });
      } finally {
        optimizingSetter(false);
      }
    } else if (file) {
      showSnackbar({ open: true, message: 'Selecciona un archivo de imagen válido (JPG, PNG, GIF, WEBP).', severity: 'warning' });
    }
  };

  const handlePortadaChange = (e) => handleFileChangeAndOptimize(e, setImagenFile, setIsOptimizingPortada, portadaInputRef);
  const handleLogoChange   = (e) => handleFileChangeAndOptimize(e, setLogoFile, setIsOptimizingLogo, logoInputRef);

  const uploadFileAndGetURL = async (fileToUpload, pathPrefix, fileType, competenciaId) => {
     if (!fileToUpload || !competenciaId) return null;
     const setIsUploading = fileType === 'logo' ? setIsUploadingLogo : setIsUploadingPortada;
     const setProgress = (progress) => setUploadProgress(prev => ({ ...prev, [fileType]: progress }));
     const finalFileName = `${fileType}_${Date.now()}.webp`;
     const storageRef = ref(storage, `${pathPrefix}/${competenciaId}/${finalFileName}`);

     setIsUploading(true);
     setProgress(0);

     return new Promise((resolve, reject) => {
       const uploadTask = uploadBytesResumable(storageRef, fileToUpload);
       uploadTask.on('state_changed',
         (snapshot) => setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
         (error) => {
           setIsUploading(false);
           setProgress(0);
           reject(new Error(`Error al subir ${fileType}. (${error.code})`));
         },
         async () => {
           try {
             const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
             setIsUploading(false);
             resolve(downloadURL);
           } catch (getUrlError) {
             setIsUploading(false);
             reject(new Error(`Error obteniendo URL para ${fileType}.`));
           }
         }
       );
     });
  };

  const deleteOldFile = async (fileUrl) => {
     if (!fileUrl || !fileUrl.startsWith("https://firebasestorage.googleapis.com/")) return;
     try {
       const fileRef = ref(storage, fileUrl);
       await deleteObject(fileRef);
     } catch (error) {
       // no-op
     }
  };

  const deleteCompetenciaFolder = async (folderPath) => {
    try {
      const folderRef = ref(storage, folderPath);
      const listResult = await listAll(folderRef);
      const deleteFilePromises = listResult.items.map(itemRef => deleteObject(itemRef));
      await Promise.all(deleteFilePromises);
    } catch (error) {
      // no-op
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!competencia || !formData) {
      showSnackbar({ open: true, message: 'Error: Faltan datos para guardar.', severity: 'error' });
      return;
    }

    setIsSaving(true);

    let finalLogoUrl = formData.general.logo;
    let finalPortadaUrl = formData.general.imagenPortada;
    const oldLogoUrl = competencia.general?.logo;
    const oldPortadaUrl = competencia.general?.imagenPortada;

    let uploadsSuccess = true;
    let errorMessages = [];

    try {
      const uploadPromises = [];
      if (logoFile) {
        uploadPromises.push(
          uploadFileAndGetURL(logoFile, 'competencias', 'logo', competencia.id)
            .then(url => { finalLogoUrl = url; })
            .catch(err => { uploadsSuccess = false; errorMessages.push(`Logo: ${err.message}`); })
        );
      }
      if (imagenFile) {
        uploadPromises.push(
          uploadFileAndGetURL(imagenFile, 'competencias', 'portada', competencia.id)
            .then(url => { finalPortadaUrl = url; })
            .catch(err => { uploadsSuccess = false; errorMessages.push(`Portada: ${err.message}`); })
        );
      }
      if (uploadPromises.length > 0) await Promise.all(uploadPromises);
      if (!uploadsSuccess) throw new Error(`Errores al subir archivos: ${errorMessages.join('. ')}`);

      const sanitizeValue = (value) => {
        if (value === undefined || value === null) return '';
        if (typeof value === 'object') return '';
        return value;
      };
      const sanitizeObject = (obj) => {
        if (!obj || typeof obj !== 'object') return {};
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) sanitized[key] = sanitizeObject(value);
          else if (Array.isArray(value)) sanitized[key] = value.map(item => (typeof item === 'object' ? sanitizeObject(item) : sanitizeValue(item)));
          else sanitized[key] = sanitizeValue(value);
        }
        return sanitized;
      };

      const updatedDataForContext = {
        id: competencia.id,
        general: sanitizeObject({
          ...(formData.general || {}),
          logo: finalLogoUrl || '',
          imagenPortada: finalPortadaUrl || '',
          precioInscripciones: parseFloat(formData.general?.precioInscripciones) || 0,
          fechaInicioCompetencia: formData.general?.fechaInicioCompetencia
            ? new Date(formData.general.fechaInicioCompetencia + 'T00:00:00Z').toISOString()
            : null,
          fechaFinCompetencia: formData.general?.fechaFinCompetencia
            ? new Date(formData.general.fechaFinCompetencia + 'T00:00:00Z').toISOString()
            : null,
          fechaInicioInscripciones: formData.general?.fechaInicioInscripciones
            ? new Date(formData.general.fechaInicioInscripciones + 'T00:00:00Z').toISOString()
            : null,
          fechaFinInscripciones: formData.general?.fechaFinInscripciones
            ? new Date(formData.general.fechaFinInscripciones + 'T00:00:00Z').toISOString()
            : null,
        }),
        location: sanitizeObject(formData.location || {}),
        contact: {
          contactos: Array.isArray(formData.contact?.contactos)
            ? formData.contact.contactos.map(contacto => sanitizeObject(contacto))
            : []
        },
        social: sanitizeObject(formData.social || {}),
        theme: sanitizeObject(formData.theme || {}),
      };

      const checkForUndefined = (obj, path = '') => {
        for (const [key, value] of Object.entries(obj)) {
          const currentPath = path ? `${path}.${key}` : key;
          if (value === undefined) throw new Error(`Valor undefined en campo: ${currentPath}`);
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) checkForUndefined(value, currentPath);
          else if (Array.isArray(value)) value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) checkForUndefined(item, `${currentPath}[${index}]`);
            else if (item === undefined) throw new Error(`Valor undefined en array: ${currentPath}[${index}]`);
          });
        }
      };
      checkForUndefined(updatedDataForContext);

      const result = await setCompetencia(updatedDataForContext);
      if (!result || !result.success) {
        const errorMsg = result?.error?.message || result?.error || 'Error desconocido en base de datos';
        throw new Error(errorMsg);
      }

      const deletePromises = [];
      if (logoFile && oldLogoUrl && oldLogoUrl !== finalLogoUrl) deletePromises.push(deleteOldFile(oldLogoUrl));
      if (imagenFile && oldPortadaUrl && oldPortadaUrl !== finalPortadaUrl) deletePromises.push(deleteOldFile(oldPortadaUrl));
      if (deletePromises.length > 0) await Promise.all(deletePromises).catch(() => {});

      showSnackbar({ open: true, message: 'Ajustes guardados exitosamente.', severity: 'success' });
      setLogoFile(null); setImagenFile(null);
      if (logoInputRef.current) logoInputRef.current.value = null;
      if (portadaInputRef.current) portadaInputRef.current.value = null;

    } catch (error) {
      showSnackbar({ open: true, message: `Error al guardar: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
      setIsUploadingLogo(false);
      setIsUploadingPortada(false);
      setUploadProgress({ logo: 0, portada: 0 });
    }
  };

  /* ───────────── Danger actions ───────────── */

  const handleOpenDeleteDialog  = () => setOpenDeleteDialog(true);
  const handleCloseDeleteDialog = () => { if (!isSaving) setOpenDeleteDialog(false); };

  const handleConfirmDelete = async () => {
    if (!competencia || !competencia.id) {
      showSnackbar({ open: true, message: 'Error: No se puede identificar la competencia a eliminar.', severity: 'error' });
      return;
    }
    setIsSaving(true);
    try {
      await deleteCompetenciaFolder(`competencias/${competencia.id}`);
      const result = await onDelete(competencia.id);
      if (!result || !result.success) throw new Error(result?.error?.message || 'Error al eliminar en base de datos.');
      showSnackbar({ open: true, message: `Competencia eliminada.`, severity: 'success' });
      navigate('/inicio', { replace: true });
    } catch (error) {
      showSnackbar({ open: true, message: `Error al eliminar: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
      setOpenDeleteDialog(false);
    }
  };

  const handleOpenCancelDialog  = () => setOpenCancelDialog(true);
  const handleCloseCancelDialog = () => { if (!isSaving) setOpenCancelDialog(false); };

  // Marca la competencia como "cancelada" conservando los datos
  const handleConfirmCancel = async () => {
    if (!competencia?.id) return;
    setIsSaving(true);
    try {
      const payload = {
        id: competencia.id,
        general: {
          ...(competencia.general || {}),
          estado: 'cancelada',
        },
      };
      const result = await setCompetencia(payload);
      if (!result?.success) throw new Error(result?.error?.message || 'No fue posible cancelar la competencia.');
      showSnackbar({ open: true, message: 'Competencia cancelada. Puedes reactivarla cambiando el estado en Ajustes.', severity: 'warning' });
    } catch (e) {
      showSnackbar({ open: true, message: `Error al cancelar: ${e.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
      setOpenCancelDialog(false);
    }
  };

  if (!formData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <SigertreLoader
          loaderText="SIGERTRE"
          message="Cargando configuración..."
          sx={{ '& .MuiTypography-root': { color: 'text.secondary', fontSize: '1.1rem', mt: 2 } }}
        />
      </Box>
    );
  }

  const isProcessing = isSaving || isOptimizingLogo || isOptimizingPortada || isUploadingLogo || isUploadingPortada;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
  sx={{
    mt: 2,
    mx: 'auto',
    width: '100%',
    // ⬇️ vuelve a ancho contenido (como Pruebas), sin desbordar en desktop
    maxWidth: { xs: '100%', lg: 1360, xl: 1600 },
    px: { xs: 0.75, sm: 1.5, md: 2 },
    // ayuda a que los hijos (grids/cards) no empujen el layout y se corte la barra horizontal
    minWidth: 0,
  }}
    >
      {/* Acordeones */}
      <CustomAccordion title="Información General" defaultExpanded showExpandIcon={false}>
        <AccordionDetails>
          <InfoGeneralForm
            formData={formData.general}
            errors={errors.general || {}}
            onChange={handleChangeGeneral}
            imagenPreview={imagenFile ? URL.createObjectURL(imagenFile) : formData.general?.imagenPortada}
            logoPreview={logoFile ? URL.createObjectURL(logoFile) : formData.general?.logo}
            handlePortadaChange={handlePortadaChange}
            handleLogoChange={handleLogoChange}
            isUploading={isUploadingPortada || isOptimizingPortada}
            isUploadingLogo={isUploadingLogo || isOptimizingLogo}
            uploadProgressPortada={uploadProgress.portada}
            uploadProgressLogo={uploadProgress.logo}
            portadaInputRef={portadaInputRef}
            logoInputRef={logoInputRef}
            competenciaId={competencia?.id}
            disabled={isProcessing}
          />
        </AccordionDetails>
      </CustomAccordion>

      <CustomAccordion title="Datos de Locación" showExpandIcon={false}>
        <AccordionDetails>
          <DatosLocacionForm
            formData={formData.location}
            errors={errors.location || {}}
            onChange={handleChangeLocation}
            disabled={isProcessing}
          />
        </AccordionDetails>
      </CustomAccordion>

      <CustomAccordion title="Información de Contacto" showExpandIcon={false}>
        <AccordionDetails>
          <InformacionContactoForm
            contactos={formData.contact?.contactos || []}
            onChange={handleContactoChange}
            onAddContacto={handleAddContacto}
            onDeleteContacto={handleDeleteContacto}
            disabled={isProcessing}
          />
        </AccordionDetails>
      </CustomAccordion>

      <CustomAccordion title="Redes Sociales" showExpandIcon={false}>
        <AccordionDetails>
          <RedesSocialesForm
            formData={formData.social}
            onChange={handleChangeSocial}
            disabled={isProcessing}
          />
        </AccordionDetails>
      </CustomAccordion>

      <CustomAccordion title="Personalización del Tema" showExpandIcon={false}>
        <AccordionDetails>
          <PersonalizacionTemaForm
            colorPrimario={formData.theme?.colorPrimario}
            colorSecundario={formData.theme?.colorSecundario}
            handleColorChange={handleChangeTheme}
            errors={errors.theme || {}}
            disabled={isProcessing}
          />
          <PrevisualizacionTema
            colorPrimario={formData.theme?.colorPrimario}
            colorSecundario={formData.theme?.colorSecundario}
            nombreEvento={formData.general?.nombre}
            lugar={formData.location?.lugar}
            fechaInicio={formData.general?.fechaInicioCompetencia}
            fechaFin={formData.general?.fechaFinCompetencia}
            logoSrc={logoFile ? URL.createObjectURL(logoFile) : formData.general?.logo}
            portadaSrc={imagenFile ? URL.createObjectURL(imagenFile) : formData.general?.imagenPortada}
            redesSociales={formData.social}
            contactos={formData.contact?.contactos}
          />
        </AccordionDetails>
      </CustomAccordion>

      {/* Acciones principales */}
      <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
        <Button
          type="submit"
          startIcon={!isSaving && <SaveIcon />}
          variant="contained"
          color="primary"
          disabled={isProcessing || !formData}
          sx={(t) => containedPill(t, 'primary')}
        >
          {isProcessing && !isSaving ? 'Procesando...' : (isSaving ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SigertreLoader loaderText="GUARDANDO" fontSize="14px" size={20} />
            </Box>
          ) : 'Guardar Cambios')}
        </Button>
      </Stack>

      {/* Zona de Peligro */}
      <Divider sx={{ my: 5 }} />
      <Box>
        <Typography variant="h6" color="error" gutterBottom>
          Zona de Peligro
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Acciones sensibles. Úsalas con precaución.
        </Typography>

        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            onClick={handleOpenCancelDialog}
            disabled={isProcessing || !competencia}
            sx={(t) => outlinePill(t, 'warning')}
          >
            Cancelar competencia
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleOpenDeleteDialog}
            disabled={isProcessing || !competencia}
            sx={(t) => outlinePill(t, 'error')}
          >
            Eliminar competencia
          </Button>
        </Stack>
      </Box>

      {/* Diálogo: Cancelar Competencia */}
      <Dialog open={openCancelDialog} onClose={handleCloseCancelDialog} aria-labelledby="cancel-dialog-title">
        <DialogTitle id="cancel-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" /> Confirmar cancelación
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta acción marcará la competencia como <strong>cancelada</strong>. Los datos se conservarán, pero
            el estado cambiará y podrás reactivarla más tarde desde Ajustes. ¿Deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCancelDialog} sx={(t) => outlinePill(t)} disabled={isSaving}>
            Volver
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="warning"
            startIcon={<BlockIcon />}
            disabled={isSaving}
            sx={(t) => containedPill(t, 'warning')}
          >
            {isSaving ? 'Aplicando…' : 'Sí, cancelar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: Eliminar Competencia */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} aria-labelledby="delete-dialog-title" aria-describedby="delete-dialog-description">
        <DialogTitle id="delete-dialog-title" sx={{ color: 'error.main' }}>
          Eliminar competencia
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {`¿Seguro que deseas eliminar '${formData?.general?.nombre ?? competencia?.nombre ?? 'esta competencia'}'? `}
            Esta acción es <strong>irreversible</strong> y borrará datos y archivos asociados.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} sx={(t) => outlinePill(t)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isSaving}
            autoFocus
            sx={(t) => containedPill(t, 'error')}
          >
            {isSaving ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SigertreLoader loaderText="ELIMINANDO" fontSize="14px" size={20} />
              </Box>
            ) : 'Sí, eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

AjustesManager.propTypes = {
  competencia: PropTypes.object,
  setCompetencia: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  setSnackbar: PropTypes.func.isRequired,
};

export default AjustesManager;
